import { useState } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

export function StatementScanWindowPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.statementScanWindow();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Statement Scan Window</CardTitle>
        <p className="text-sm text-muted-foreground">
          Checks whether the Gmail statement scan lookback window covers the scheduled run gap.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={runCheck} disabled={loading} size="sm">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Check
        </Button>

        {results && (
          <div className="space-y-4">
            {/* Verdict */}
            <div className="flex items-center gap-3">
              {results.verdict === 'ok' ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0" />
              )}
              <Badge className={results.verdict === 'ok'
                ? 'bg-green-600 text-white border-transparent'
                : 'bg-orange-500 text-white border-transparent'
              }>
                {results.verdict}
              </Badge>
              <span className="text-sm">{results.reason}</span>
            </div>

            {/* Metrics grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Scan frequency</div>
                <div className="font-mono font-semibold">{results.scan_frequency_hours}h</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{results.raw_interval}</div>
              </div>
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Lookback window</div>
                <div className="font-mono font-semibold">{results.lookback_window_hours}h</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{results.raw_cutoff_expression}</div>
              </div>
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Safe threshold (2x gap)</div>
                <div className="font-mono font-semibold">{results.safe_threshold_hours}h</div>
              </div>
            </div>

            {/* Limitation note */}
            <div className="text-xs text-muted-foreground bg-muted/50 border rounded p-3">
              <span className="font-semibold">Known limitation: </span>
              {results.limitation_note}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
